/**
 * @typedef {Object} CollectionFilter
 *
 * @property {string} label
 * @property {string} param
 * @property {CollectionValue[]} values
 */

/**
 * @typedef {Object} CollectionValue
 *
 * @property {string} label
 * @property {string} param
 * @property {string} urlToAdd
 * @property {string} urlToRemove
 * @property {string} value
 */

class GiftFinderRenderer {
  /**
   * @param {GiftFinderRequester} requester
   */
  constructor (requester) {
    this.requester = requester
  }

  /**
   * @param {string} html
   */
  render (html) {
    document.dispatchEvent(new CustomEvent('BEFORE_GIFT_FINDER_RENDER'))
    const dom = new DOMParser().parseFromString(html, 'text/html')

    this._renderProducts(dom)
    this._renderSelected(dom)
    this._renderQuestions(dom)

    this._renderData(dom)

    this.requester.giftFinder.dataset.hasSelected = this.requester.giftFinder.hasSelected.toString()

    if (this.requester.giftFinder.dataset.hasSelected === false) {
      this.requester.giftFinder.selectedDrawerIsOpen = false
    }

    this.requester.giftFinder.replaceName()
    document.dispatchEvent(new CustomEvent('AFTER_GIFT_FINDER_RENDER'))
  }

  /**
   * @param {Document} dom
   */
  _renderProducts (dom) {
    this._renderSingleSimple(dom, this.giftFinder.els.products.selector)

    if (this.giftFinder.els?.['product-list']?.exists) {
      const productListEl = window.BAO.Utils.getElement(
        this.giftFinder.els['product-list'].selector,
        { context: this.giftFinder.els.products.element },
      )

      productListEl.append(
        ...Array.from(productListEl.children).sort(() => {
          return Math.random() - 0.5
        }),
      )
    }
  }

  /**
   * @param {Document} dom
   */
  _renderSelected (dom) {
    this._renderSingleSimple(dom, this.giftFinder.els.selected.selector)
  }

  _renderQuestions (dom) {
    const currentQuestions = this._getCurrentElements(
      this.giftFinder.els.question.selector,
    )
    if (currentQuestions.length === 0) return

    const newQuestions = this._getNewElements(
      dom,
      this.giftFinder.els.question.selector,
    )
    if (newQuestions.length === 0) return

    currentQuestions.forEach((el, index) =>
      this._renderQuestion(el, newQuestions[index]),
    )
  }

  _renderQuestion (currentDom, newDom) {
    const currentNo = currentDom.querySelector(currentDom.els.no.selector)
    const newNo = newDom.querySelector(currentDom.els.no.selector)
    currentNo.setAttribute('data-url', newNo.dataset.url)

    const currentYes = currentDom.querySelector(currentDom.els.yes.selector)
    const newYes = newDom.querySelector(currentDom.els.yes.selector)
    currentYes.setAttribute('data-url', newYes.dataset.url)

    const currentTitle = currentDom.querySelector(currentDom.els.title.selector)
    const newTitle = newDom.querySelector(currentDom.els.title.selector)
    currentTitle.textContent = newTitle.textContent
  }

  /**
   * @param {Document} dom
   */
  _renderData (dom) {
    this._renderSingleSimple(dom, this.giftFinder.els.json.selector)
  }

  /**
   * @param {Document} dom
   * @param {string} selector
   */
  _renderSingleSimple (dom, selector) {
    const currentEl = this._getCurrentElement(selector)
    if (!currentEl) return

    const newEl = this._getNewElement(dom, selector)
    if (!newEl) return

    currentEl.outerHTML = newEl.outerHTML
  }

  /**
   * @param {Document} dom
   * @param {string} selector
   */
  _renderMultipleSimple (dom, selector) {
    const currentEls = this._getCurrentElements(selector)
    if (currentEls.length === 0) return

    const newEls = this._getNewElements(dom, selector)
    if (newEls.length === 0) return

    currentEls.forEach((el, index) => (el.outerHTML = newEls[index].outerHTML))
  }

  /**
   * @param {string} selector
   *
   * @returns {HTMLElement|null}
   */
  _getCurrentElement (selector) {
    return window.BAO.Utils.getElement(selector)
  }

  /**
   * @param {string} selector
   *
   * @returns {Array<Element>|NodeListOf<Element>}
   */
  _getCurrentElements (selector) {
    return window.BAO.Utils.getElements(selector)
  }

  /**
   * @param {Document} dom
   * @param {string} selector
   *
   * @returns {HTMLElement|null}
   */
  _getNewElement (dom, selector) {
    return window.BAO.Utils.getElement(selector, { context: dom })
  }

  /**
   * @param {Document} dom
   * @param {string} selector
   *
   * @returns {Array<Element>|NodeListOf<Element>}
   */
  _getNewElements (dom, selector) {
    return window.BAO.Utils.getElements(selector, { context: dom })
  }

  get giftFinder () {
    return this.requester.giftFinder
  }
}

class GiftFinderRequester {
  /**
   * @param {GiftFinder} giftFinder
   */
  constructor (giftFinder) {
    this.giftFinder = giftFinder

    this.renderer = new GiftFinderRenderer(this)

    /**
     * @type {FilterData[]}
     * @public
     */
    this.filterData = []

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }
  }

  /**
   * @param {string} url
   * @param {string} section
   * @param {string|null} [question]
   * @param {boolean} [updateURLHash]
   */
  async request (url, section, question = null, updateURLHash = true) {
    const searchParamsString = url.includes('?')
      ? url.slice(url.indexOf('?') + 1)
      : ''
    const searchParams = new URLSearchParams(searchParamsString)

    if (question) {
      searchParams.set('question', question)
    } else {
      searchParams.delete('question')
    }

    if (updateURLHash) {
      this.updateURLHash(searchParams)
    }

    if (this.filterData.some(datum => datum.url === url)) {
      return this._fromCache(url)
    }

    return this._fromFetch(section, url)
  }

  /**
   * @param {string} html
   */
  _render (html) {
    this.giftFinder.log('_render', { html })

    this.renderer.render(html)
  }

  /**
   * @param {string} url
   *
   * @returns {void}
   */
  _fromCache (url) {
    const html = this.filterData.find(datum => datum.url === url).html

    this.giftFinder.log('fromCache', { url, html })

    this._render(html)
  }

  /**
   * @param {string} section
   * @param {string} url
   *
   * @returns {void}
   */
  async _fromFetch (section, url) {
    const { html } = await this.cacheUrl(section, url)

    this._render(html)
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${searchParams.toString() &&
        '?'.concat(searchParams.toString())}`,
    )
  }

  /**
   * @param {string} section
   * @param {string} url
   *
   * @returns {{ html: string, url: string }}
   */
  async cacheUrl (section, url) {
    const alreadyHasBeenCached = this.filterData.find(data => data.url === url)

    if (alreadyHasBeenCached) {
      this.giftFinder.log('alreadyCached', {
        section,
        url,
        html: alreadyHasBeenCached.html,
      })

      return alreadyHasBeenCached
    }

    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.giftFinder.log('cacheUrl', { section, url, html })

    this.filterData.push({ html, url })

    return { html, url }
  }
}

class GiftFinder extends window.BAO.CustomElement() {
  static get styles () {
    return `
      :host {
        display: block;
      }
    `
  }

  static get requiredElements () {
    return ['giftFinderIntro', 'json', 'products', 'trigger']
  }

  checkValidity () {
    super.checkValidity()

    if (!this.sectionName) {
      this.throwError(
        `needs a \`data-section-id="{{ section.id }}"\` attribute`,
      )
    }
  }

  connectedCallback () {
    super.connectedCallback()

    this.quizHasStarted = false
    this.quizIsOpen = true

    this.questionIndex = this.startAtQuestionIndex

    this.requester = new GiftFinderRequester(this)

    /**
     * @type {({ filter: CollectionFilter, value: CollectionValue }|null)[]}
     * @public
     */
    this.answers = []

    this.preloadImages()

    this.log('Gift Finder Data', this.data)

    if (this.shouldStartAtQuestion) {
      window.setTimeout(() => {
        const question = this.getQuestionByIndex(this.questionIndex)
        question.activate()

        this.quizHasStarted = true
      }, 100)
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onTriggerClick = this.onTriggerClick.bind(this)
    this.startQuiz = this.startQuiz.bind(this)

    this.listeners.add(this.els.trigger.element, 'click', this.onTriggerClick)

    if (this.els?.sort?.exists) {
      this.onSortChange = this.onSortChange.bind(this)

      this.listeners.add(this.els.sort.element, 'change', this.onSortChange)
    }

    this.onClearClick = this.onClearClick.bind(this)
    this.listeners.add(this, 'click', this.onClearClick)

    this.onSelectionCloseClick = this.onSelectionCloseClick.bind(this)
    this.listeners.add(this, 'click', this.onSelectionCloseClick)

    if (this.els?.reset?.element) {
      this.onResetClick = this.onResetClick.bind(this)
      this.listeners.add(this.els.reset.element, 'click', this.onResetClick)
    }

    this.listeners.add(window, 'popstate', async event => {
      const isASpecialCase = !event.state || event?.state?.searchParams === ''

      if (isASpecialCase) {
        if (this.questionIndex > 0) {
          this.questionIndex -= 1

          this.loading = true

          resetQuestion(this, this.questionIndex)
          await resetState(this)

          this.loading = false

          return this.goToQuestion(this.questionIndex)
        } else if (this.questionIndex === 0) {
          this.quizHasStarted = false
          this.answers = []

          await this.requester.request(
            window.location.pathname,
            this.sectionName,
            null,
            false,
          )

          this.giftFinderIntro.show()

          this.questions.forEach(question =>
            question.parent.setAttribute('aria-hidden', 'true'),
          )

          window.BAO.Cart.updateCartAttributes(
            {
              _gift_finder_question: null,
            },
            {
              sections: [],
            },
          )

          return
        }

        return
      }

      if (!('searchParams' in event.state)) return

      const searchParams = new URLSearchParams(event.state.searchParams)
      const questionIndex = Number(searchParams.get('question'))

      if (Number.isNaN(questionIndex)) return

      this.loading = true

      resetQuestion(this, questionIndex)
      await resetState(this)

      this.loading = false

      this.goToQuestion(questionIndex)

      function resetQuestion (giftFinder, questionIndex) {
        giftFinder.questionIndex = questionIndex
        const question = giftFinder.getQuestionByIndex(giftFinder.questionIndex)

        giftFinder.removeAnswer(question.index)
      }

      async function resetState (giftFinder) {
        const url = `${window.location.pathname}${window.location.search}`

        await giftFinder.requester.request(
          url,
          giftFinder.sectionName,
          `${giftFinder.questionIndex}`,
          false,
        )
      }
    })
  }

  onTriggerClick () {
    this.quizIsOpen = !this.quizIsOpen
  }

  async onClearClick (event) {
    const target = event.target.closest(this.els?.clear?.selector)
    if (!target) return

    this.loading = true

    const url = new URL(window.location.href)
    const keys = [...url.searchParams.keys()]

    keys
      .filter(key => key.startsWith('filter.'))
      .forEach(key => url.searchParams.delete(key))

    this.answers = []
    this.selectedDrawerIsOpen = false

    await this.requester.request(url.toString(), this.sectionName)

    this.loading = false
  }

  async onSortChange (event) {
    this.loading = true

    const url = new URL(window.location.href)
    url.searchParams.set(event.target.name, event.target.value)

    await this.requester.request(
      url.toString(),
      this.sectionName,
      this.currentQuestionFromParam,
    )

    this.loading = false
  }

  onSelectionCloseClick (event) {
    const target = event.target.closest(this.els?.selectedClose?.selector)
    if (!target) return

    this.selectedDrawerIsOpen = false
  }

  async onResetClick () {
    document.dispatchEvent(new CustomEvent('BEFORE_GIFT_FINDER_RESET'))
    this.loading = true

    const url = new URL(window.location.href)
    const keys = [...url.searchParams.keys()]

    keys
      .filter(key => key.startsWith('filter.'))
      .forEach(key => url.searchParams.delete(key))

    this.answers = []

    await this.requester.request(url.toString(), this.sectionName)

    const parent = this.els.finale.element.closest('[data-parent]')
    parent.setAttribute('aria-hidden', 'true')

    this.questionIndex = 0
    this.quizHasStarted = false
    this.quizHasEnded = false

    this.els.questions.element.setAttribute('data-final-pane-visible', 'false')
    this.giftFinderIntro.show()

    this.loading = false
    document.dispatchEvent(new CustomEvent('AFTER_GIFT_FINDER_RESET'))
  }

  startQuiz () {
    this.giftFinderIntro.hide()

    const question = this.getQuestionByIndex(this.questionIndex)
    question.activate()

    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set('question', '0')

    this.requester.updateURLHash(searchParams)

    this.quizHasStarted = true
  }

  replaceName () {
    this.questions.forEach(question => {
      const titleEl = window.BAO.Utils.getElement(question.els.title.selector, {
        context: question,
      })
      if (!titleEl) return

      titleEl.textContent = titleEl.dataset.titleText.replace(
        '[[name]]',
        this.giftFinderIntro.name,
      )
    })
  }

  /**
   * @param {number} index
   * @param {CollectionFilter} question
   * @param {CollectionValue} answer
   */
  registerAnswer (index, question, answer) {
    if (question === undefined && answer === undefined) {
      this.answers[index] = null
    } else {
      this.answers[index] = { filter: question, value: answer }
    }

    this.log('Answers', this.answers)
  }

  /**
   * @param {number} index
   */
  removeAnswer (index) {
    delete this.answers[index]

    const remainingAnswers = this.answers.filter(Boolean)
    if (remainingAnswers.length === 0) {
      this.selectedDrawerIsOpen = false
    }
  }

  nextQuestion () {
    this.questionIndex += 1

    const question = this.getQuestionByIndex(this.questionIndex)
    question.activate()

    this.loading = false
  }

  goToQuestion (index) {
    this.questionIndex = index

    if (index < this.questions.length) {
      this.hideFinalPane()
    }

    const question = this.getQuestionByIndex(this.questionIndex)
    question.activate()

    this.loading = false
  }

  showFinalPane () {
    this.questions.forEach(el => {
      el.parent.setAttribute('aria-hidden', 'true')
    })

    const parent = this.els.finale.element.closest('[data-parent]')
    parent.setAttribute('aria-hidden', 'false')

    this.quizHasEnded = true
  }

  hideFinalPane () {
    const parent = this.els.finale.element.closest('[data-parent]')
    parent.setAttribute('aria-hidden', 'true')

    this.quizHasEnded = false
  }

  getQuestionByIndex (index = 0) {
    this.replaceName()
    return this.questions[index]
  }

  preloadImages () {
    const images = window.BAO.Utils.getElements('img.rsp-Image_Image', {
      context: this.els.questions.element,
    })

    images.forEach(el => {
      const image = new Image()
      image.onload = () => this.log('Image loaded', { el })

      if (el.hasAttribute('sizes')) {
        image.sizes = el.getAttribute('sizes')
      }

      if (el.hasAttribute('srcset')) {
        image.srcset = el.getAttribute('srcset')
      }
    })
  }

  set quizHasEnded (boolean) {
    this._quizHasEnded = boolean
    this.els.questions.element.setAttribute(
      'data-quiz-has-ended',
      String(boolean),
    )
  }

  get quizHasEnded () {
    return this._quizHasEnded
  }

  get giftFinderIntro () {
    return this.els.giftFinderIntro.element
  }

  get questions () {
    return this.els.question.elements
  }

  get data () {
    return JSON.parse(this.els.json.element.innerHTML)
  }

  get quizIsOpen () {
    return this.dataset.quizIsOpen === 'true'
  }

  set quizIsOpen (val) {
    this.dataset.quizIsOpen = val.toString()
  }

  get hasSelected () {
    return this.answers.some(answer => !!answer)
  }

  get sectionName () {
    return this.dataset.sectionId
  }

  get currentQuestionFromParam () {
    return new URLSearchParams(window.location.search).get('question')
  }

  get shouldStartAtQuestion () {
    return this.dataset.shouldStartAtQuestion === 'true'
  }

  get startAtQuestionIndex () {
    return Number(this.dataset.startAtQuestionIndex) || 0
  }

  get loading () {
    return this.getAttribute('aria-busy') === 'true'
  }

  set loading (val) {
    this.setAttribute('aria-busy', val.toString())
  }

  get name () {
    return this.giftFinderIntro.name
  }

  get selectedDrawerIsOpen () {
    return this.dataset.selectedDrawerIsOpen === 'true'
  }

  set selectedDrawerIsOpen (val) {
    this.dataset.selectedDrawerIsOpen = val.toString()
  }

  get questionIndex () {
    return this._questionIndex
  }

  set questionIndex (val) {
    this._questionIndex = val

    this.els.questions.element.setAttribute(
      'data-quiz-has-started',
      String(val !== 0),
    )

    window.BAO.Cart.updateCartAttributes(
      {
        _gift_finder_question: val,
      },
      {
        sections: [],
      },
    )
  }
}

if (!customElements.get('gift-finder')) {
  customElements.define('gift-finder', GiftFinder)
}

class GiftFinderIntro extends window.BAO.CustomElement() {
  static get styles () {
    return `
      :host {
        display: block;
      }
    `
  }

  static get requiredElements () {
    return ['nameForm', 'nameInput']
  }

  connectedCallback () {
    super.connectedCallback()

    this.onNameInput = this.onNameInput.bind(this)
    this.onNameFormSubmit = this.onNameFormSubmit.bind(this)

    this.listeners.add(this.els.nameInput.element, 'input', this.onNameInput)
    this.listeners.add(
      this.els.nameForm.element,
      'submit',
      this.onNameFormSubmit,
    )

    window.setTimeout(() => {
      this.setAttribute('data-ready', 'true')
    }, 50)
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(document, 'BEFORE_GIFT_FINDER_RESET', () => {
      this.els.nameInput.element.value = ''
      this.updateName('')
    })
  }

  onNameInput (event) {
    this.name = event.target.value
  }

  onNameFormSubmit (event) {
    event.preventDefault()

    this.updateName(this.name)
    this.giftFinder.startQuiz()
  }

  updateName (name) {
    window.BAO.Cart.updateCartAttributes(
      {
        _gift_finder_name: name,
      },
      {
        sections: [],
      },
    )
  }

  hide () {
    this.parent.setAttribute('aria-hidden', 'true')

    this.parent.style.display = 'none'
  }

  show () {
    this.parent.setAttribute('aria-hidden', 'false')

    this.parent.style.display = ''
  }

  get giftFinder () {
    return this.closest('gift-finder')
  }

  get name () {
    return this.dataset.name
  }

  set name (val) {
    this.setAttribute('data-name', val)
  }

  get parent () {
    return this.closest('[data-parent]')
  }
}

if (!customElements.get('gift-finder-intro')) {
  customElements.define('gift-finder-intro', GiftFinderIntro)
}

class GiftFinderQuestion extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['no', 'yes']
  }

  static get styles () {
    return `
      :host {
        display: block;
      }
    `
  }

  checkValidity () {
    super.checkValidity()

    if (Number.isNaN(this.index)) {
      this.throwError(`needs a data-index="{number}" value`)
    }

    if (!this.filter) {
      this.throwError(`can't find filter for ${this.dataset.paramName}`)
    }

    if (!this.answer) {
      this.throwError(`can't find answer for ${this.dataset.answer}`)
    }
  }

  setupListeners () {
    super.setupListeners()

    this.answerQuestion = this.answerQuestion.bind(this)
    this.onYesClick = this.onYesClick.bind(this)
    this.onNoClick = this.onNoClick.bind(this)

    this.listeners.add(this.els.no.element, 'click', this.onNoClick)
    this.listeners.add(this.els.yes.element, 'click', this.onYesClick)

    if (this.els?.selectionTrigger?.element) {
      this.onSelectionClick = this.onSelectionClick.bind(this)

      this.listeners.add(
        this.els.selectionTrigger?.element,
        'click',
        this.onSelectionClick,
      )
    }

    if (this.els?.skip?.element) {
      this.onSkipClick = this.onSkipClick.bind(this)

      this.listeners.add(this.els.skip?.element, 'click', this.onSkipClick)
    }
  }

  async onNoClick () {
    this.giftFinder.loading = true
    this.els.no.element.setAttribute('aria-busy', 'true')

    const selectedElement = window.BAO.Utils.getElement(
      `gift-finder-selected[data-index="${this.index}"]`,
    )

    if (selectedElement) {
      await selectedElement.onRemoveClick()
    } else {
      await new Promise(resolve => setTimeout(() => resolve(), 500))
    }

    this.onSkipClick()
    this.giftFinder.loading = false
    this.els.no.element.setAttribute('aria-busy', 'false')
  }

  async onYesClick () {
    if (this.giftFinder.loading) return

    this.giftFinder.loading = true
    this.els.yes.element.setAttribute('aria-busy', 'true')

    await this.answerQuestion()

    this.giftFinder.loading = false
    this.els.yes.element.setAttribute('aria-busy', 'false')
  }

  async answerQuestion () {
    const url = this.els.yes.element.dataset.url
    if (!url) {
      throw new Error(`data-url must exist on ${this.els.yes.selector} element`)
    }

    // Need to capture it because when this question gets re-rendered it's not in the DOM anymore,
    // so it won't work
    const giftFinder = this.giftFinder

    giftFinder.registerAnswer(this.index, this.filter, this.answer)

    await giftFinder.requester.request(
      url,
      giftFinder.sectionName,
      `${giftFinder.questionIndex + 1}`,
    )

    giftFinder.loading = false

    if (!this.isLastQuestion) {
      return giftFinder.nextQuestion()
    }

    return giftFinder.showFinalPane()
  }

  onSkipClick () {
    const giftFinder = this.giftFinder

    if (!this.isLastQuestion) {
      return giftFinder.nextQuestion()
    }

    return giftFinder.showFinalPane()
  }

  onSelectionClick () {
    this.giftFinder.selectedDrawerIsOpen = true
  }

  activate () {
    this.giftFinder.questions.forEach(el =>
      el.parent.setAttribute('aria-hidden', (el !== this).toString()),
    )

    window.setTimeout(() => {
      this.setAttribute('data-ready', 'true')

      this.prefetchAnswerUrls()
    }, 50)
  }

  prefetchAnswerUrls () {
    const urls = [this.answer.urlToAdd, this.answer.urlToRemove]

    Promise.all(
      urls.map(url =>
        this.giftFinder.requester.cacheUrl(this.giftFinder.sectionName, url),
      ),
    )
  }

  get giftFinder () {
    return (this._giftFinder = this._giftFinder || this.closest('gift-finder'))
  }

  get parent () {
    return this.closest('[data-parent]')
  }

  get index () {
    return Number(this.dataset.index)
  }

  get filter () {
    return this.giftFinder.data.filters.find(
      filter => filter.param === this.dataset.paramName,
    )
  }

  get answer () {
    return this.filter.values.find(
      answer => answer.value === this.dataset.answer,
    )
  }

  get isLastQuestion () {
    return this.index === this.giftFinder.questions.length - 1
  }
}

if (!customElements.get('gift-finder-question')) {
  customElements.define('gift-finder-question', GiftFinderQuestion)
}

class GiftFinderSelected extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['remove']
  }

  static get styles () {
    return `
      :host {
        display: block;
      }
    `
  }

  setupListeners () {
    super.setupListeners()

    this.onRemoveClick = this.onRemoveClick.bind(this)

    this.listeners.add(this.els.remove.element, 'click', this.onRemoveClick)
  }

  async onRemoveClick () {
    this.giftFinder.loading = true

    const url = this.els.remove.element.dataset.url

    // Need to capture it because when this question gets re-rendered it's not in the DOM anymore,
    // so it won't work
    const giftFinder = this.giftFinder

    giftFinder.removeAnswer(this.index)

    await giftFinder.requester.request(
      url,
      giftFinder.sectionName,
      this.giftFinder.currentQuestionFromParam,
    )

    giftFinder.loading = false
  }

  get giftFinder () {
    return this.closest('gift-finder')
  }

  get index () {
    return Number(this.dataset.index)
  }
}

if (!customElements.get('gift-finder-selected')) {
  customElements.define('gift-finder-selected', GiftFinderSelected)
}

class GiftFinderHelper extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['answers', 'input', 'toggle']
  }

  setupListeners () {
    super.setupListeners()

    this.onInputClick = this.onInputClick.bind(this)
    this.onToggleClick = this.onToggleClick.bind(this)

    this.listeners.add(this, 'click', this.onInputClick)
    this.listeners.add(this.els.toggle.element, 'click', this.onToggleClick)

    const section = this.closest('[data-shopify-editor-section]')
    if (section) {
      const sectionId = this.closest('[data-section-id]').dataset.sectionId

      document.addEventListener('shopify:section:select', e => {
        if (e.detail.sectionId !== sectionId) return

        this.show()
      })

      document.addEventListener('shopify:block:select', e => {
        if (e.detail.sectionId !== sectionId) return

        this.show()
      })
    }
  }

  onInputClick (event) {
    const target = event.target.closest(this.els.input.selector)
    if (!target) return

    target.setSelectionRange(0, target.value.length)

    const answers = target.parentNode.querySelector(this.els.answers.selector)
    if (!answers) return

    const allAnswers = window.BAO.Utils.getElements(this.els.answers.selector, {
      context: this,
    })
    allAnswers.forEach(el =>
      el.setAttribute('aria-hidden', (el !== answers).toString()),
    )
  }

  onToggleClick () {
    const shouldShow = this.getAttribute('aria-expanded') !== 'true'

    shouldShow ? this.show() : this.hide()
  }

  show () {
    this.setAttribute('aria-expanded', 'true')

    this.els.toggle.element.textContent = 'Hide'
  }

  hide () {
    this.setAttribute('aria-expanded', 'false')

    this.els.toggle.element.textContent = 'Show'
  }
}

if (!customElements.get('gift-finder-helper')) {
  customElements.define('gift-finder-helper', GiftFinderHelper)
}

class GiftFinderModal extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['body', 'close', 'save', 'leave']
  }

  connectedCallback () {
    super.connectedCallback()

    this.href = null
    this.wishlistId = false
  }

  setupListeners () {
    this.onModalClick = this.onModalClick.bind(this)
    this.onMouseOut = this.onMouseOut.bind(this)
    this.linkInterceptor = this.linkInterceptor.bind(this)
    this.onLeaveClick = this.onLeaveClick.bind(this)
    this.onSaveClick = this.onSaveClick.bind(this)

    this.listeners.add(this.els.close.element, 'click', () => this.close())
    this.listeners.add(this, 'click', this.onModalClick)

    this.listeners.add(document, 'mouseout', this.onMouseOut)
    this.listeners.add(document, 'click', this.linkInterceptor)

    this.listeners.add(this.els.leave.element, 'click', this.onLeaveClick)
    this.listeners.add(this.els.save.element, 'click', this.onSaveClick)
  }

  onModalClick (event) {
    const target = event.target.closest(this.els.body.selector)
    if (target) return

    this.close()
  }

  onMouseOut (event) {
    if (!this.shouldTrigger) return

    const isTryingToLeave = !(event.toElement && event.relatedTarget)
    if (!isTryingToLeave) return

    this.open()
  }

  linkInterceptor (event) {
    if (!this.shouldTrigger) return

    const target = event.target.closest('a')
    if (!target) return

    this.href = target.getAttribute('href')

    event.preventDefault()

    this.open()
  }

  onLeaveClick () {
    if (this.href != null) {
      window.location.href = this.href

      this.close()

      return
    }

    this.close()
  }

  async onSaveClick () {
    document.dispatchEvent(new CustomEvent('BEFORE_GIFT_FINDER_SAVE'))
    this.els.save.element.setAttribute('aria-busy', 'true')

    if (window.BAO.wishlist.token === '') {
      window.BAO.wishlist.redirectToLogin()
      return
    }

    try {
      const token = window.BAO.wishlist.token

      const variantIds = this.inactiveHearts.map(
        heart => heart.dataset.variantId,
      )

      const listFormData = new FormData()
      listFormData.set('name', this.giftFinder.name)

      if (!this.wishlistId) {
        const data = await window.BAO.wishlist.createList(listFormData, token)
        this.wishlistId = data.unique_id
      }

      await window.BAO.wishlist.addMultipleToList(
        variantIds,
        this.wishlistId,
        token,
      )

      this.inactiveHearts.forEach(heart => heart.markActive())
    } catch (e) {
      console.error(`Failed to save products`)
    } finally {
      this.els.save.element.setAttribute('aria-busy', 'false')
      this.close()
    }
    document.dispatchEvent(new CustomEvent('AFTER_GIFT_FINDER_SAVE'))
    /* Reload the page as list data needs to be re-rendered to the page */
    window.location.reload()
  }

  get inactiveHearts () {
    const hearts = window.BAO.Utils.getElements(
      'wishlist-heart, [is="wishlist-heart"]',
      {
        context: this.giftFinder,
      },
    )
    return hearts.filter(heart => !heart.active)
  }

  open () {
    this.setAttribute('aria-hidden', 'false')
  }

  close () {
    this.setAttribute('aria-hidden', 'true')
  }

  get giftFinder () {
    return this.closest('gift-finder')
  }

  get shouldTrigger () {
    /*
    return (
      this.giftFinder.quizHasStarted &&
      this.giftFinder.questionIndex > 1 &&
      !!this.inactiveHearts.length
    )
    */
    /* Temporailly disable wishlist modal - This will be added back in */
    return false
  }
}

if (!customElements.get('gift-finder-modal')) {
  customElements.define('gift-finder-modal', GiftFinderModal)
}
